/**
 * Sprint T-18 · Notification Studio · Shared JSON editor.
 *
 * Minimal <textarea> monospace editor with:
 *   - live JSON validation (parse errors shown inline)
 *   - format-on-blur (2-space indent)
 *   - controlled component: value + onChange({raw, parsed, error})
 *
 * Zero dependencies (Q4 = a). Reused by Playground and Template Studio.
 */
import React, { useState, useEffect, useMemo } from "react";

export default function JsonEditor({
  value,
  onChange,
  placeholder = "{}",
  testid = "studio-json-editor",
  rows = 12,
  label,
}) {
  const [raw, setRaw] = useState(
    typeof value === "string" ? value : JSON.stringify(value ?? {}, null, 2),
  );
  const [error, setError] = useState(null);

  // keep in sync if parent value changes explicitly
  useEffect(() => {
    if (typeof value === "string") {
      setRaw(value);
    } else if (value && typeof value === "object") {
      setRaw(JSON.stringify(value, null, 2));
    }
  }, [value]);

  const parsed = useMemo(() => {
    try {
      if (!raw.trim()) return {};
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }, [raw]);

  const handleChange = (newRaw) => {
    setRaw(newRaw);
    try {
      const parsedNew = newRaw.trim() ? JSON.parse(newRaw) : {};
      setError(null);
      onChange?.({ raw: newRaw, parsed: parsedNew, error: null });
    } catch (e) {
      setError(e.message);
      onChange?.({ raw: newRaw, parsed: null, error: e.message });
    }
  };

  const handleBlur = () => {
    if (parsed !== null) {
      const formatted = JSON.stringify(parsed, null, 2);
      if (formatted !== raw) {
        setRaw(formatted);
        onChange?.({ raw: formatted, parsed, error: null });
      }
    }
  };

  return (
    <div className="flex flex-col gap-1" data-testid={`${testid}-wrap`}>
      {label && (
        <div className="flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-widest text-[var(--ag-text-dim)]">
            {label}
          </span>
          {error && (
            <span
              className="font-mono text-[10px] text-rose-400"
              data-testid={`${testid}-error`}
            >
              JSON: {error}
            </span>
          )}
        </div>
      )}
      <textarea
        rows={rows}
        value={raw}
        onChange={(e) => handleChange(e.target.value)}
        onBlur={handleBlur}
        placeholder={placeholder}
        spellCheck={false}
        data-testid={testid}
        className={`w-full resize-y rounded border font-mono text-xs leading-relaxed
          bg-black/30 px-3 py-2 outline-none
          ${
            error
              ? "border-rose-500/60 text-rose-200"
              : "border-[var(--ag-border)] text-[var(--ag-text)] focus:border-[var(--ag-gold)]/60"
          }
        `}
      />
    </div>
  );
}
